import React, {
    useEffect,
    useState,
    useMemo,
    forwardRef,
    useImperativeHandle,
    useRef,
} from "react";

import Button from "./Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import axios from "axios";
import Wheel from "./Wheel";
import ProgressBar from "./ProgressBar";
import { maximumSession, minimumSession } from "@bd-utils/gameVariable";
import {
    getStoredTimer,
    setStoredTimer,
    getIsGameActive,
    setIsGameActive,
    setIsScoringEnabled,
} from "../utils/storage";
import { UserConsumer } from "./Context";
import { Link, useLocation } from "react-router-dom";
import LanguageOptionMenu from "./LanguageOptionMenu";
import DecorativeLines3 from "./DecorativeLines3";

const DEFAULT_SESSION_STEPS = [15, 30, 45, 60, 75, 90, 120];

const generateSessionSteps = (min, max, stepCount = 7) => {
    const minVal = parseInt(min, 10);
    const maxVal = parseInt(max, 10);
    if (isNaN(minVal) || isNaN(maxVal) || minVal >= maxVal) {
        return DEFAULT_SESSION_STEPS;
    }
    const steps = [];
    for (let i = 0; i < stepCount; i++) {
        const val = Math.round(
            minVal + (i * (maxVal - minVal)) / (stepCount - 1),
        );
        steps.push(val);
    }
    return steps;
};

const getInitialStepIndex = (steps, targetVal) => {
    if (!steps || steps.length === 0) return 0;
    if (targetVal !== undefined && targetVal !== null && !isNaN(targetVal)) {
        let closestIdx = 0;
        let minDiff = Infinity;
        steps.forEach((step, idx) => {
            const diff = Math.abs(step - targetVal);
            if (diff < minDiff) {
                minDiff = diff;
                closestIdx = idx;
            }
        });
        return closestIdx;
    }
    return Math.floor(steps.length / 2);
};

const intervalTime = (maximumSession - minimumSession) / 10;

const Item = (props) => {
    const [fadeOut, setFadeout] = useState(false);

    const fadingOut = () => {
        setFadeout(true);
    };

    const onRemoveMember = () => {
        props.removeItem();
        setFadeout(false);
    };
    return (
        <div
            className={`instruction-setup__member-item ${
                fadeOut ? "instruction-setup__member-item--fadeout" : ""
            }`}
        >
            <div className="instruction-setup__member-name">
                <span>{props.item}</span>
            </div>
            <button
                type="button"
                className="instruction-setup__member-delete"
                onClick={() =>
                    fadingOut(setTimeout(() => onRemoveMember(), 300))
                }
            >
                <FontAwesomeIcon className="trash-icon" icon={faTrash} />
            </button>
        </div>
    );
};

const introductionScreen = forwardRef(
    (
        {
            isOpen,
            onClose,
            onStart,
            members,
            categoryList,
            wheel,
            teamMembers,
            text,
            next,
            textInstruction,
            langSwitcher,
            instructionPageButton,
            playGameButton,
            isDisable,
            minimumSession,
            maximumSession,
            standardSessionTime,
            selectedTimer,
            onChangeTimer,
            onRestartSession,
            isGameActive,
            isBlur,
            onOpenPassword,
            onSetPlay,
            login,
            usePassword,
            onFinishGame,
        },
        ref,
    ) => {
        const location = useLocation();
        const isSetupFromLocation = Boolean(location?.state?.setupTeam);
        const [teamName, setTeamName] = useState("");
        const [teamMemberName, setTeamMemberName] = useState("");
        const [isSetupSection, setIsSetupSection] =
            useState(isSetupFromLocation);
        const [isOpen_warning, setIsOpen_warning] = useState(false);
        const [isOpen_inputWarning, setOpen_inputWarning] = useState(false);
        const [warningType, setWarningType] = useState(null);
        const [isAlert, setAlert] = useState("");
        const [isDisabled, setDisabled] = useState(false);
        const sessionSteps = useMemo(
            () => generateSessionSteps(minimumSession, maximumSession),
            [minimumSession, maximumSession],
        );

        const initialTimerVal =
            selectedTimer || getStoredTimer(standardSessionTime);
        const [stepIndex, setStepIndex] = useState(() =>
            getInitialStepIndex(
                sessionSteps,
                initialTimerVal || standardSessionTime,
            ),
        );
        const [timerValue, setTimerValue] = useState(
            () => sessionSteps[stepIndex] || initialTimerVal || sessionSteps[0],
        );
        const [showRestartWarning, setShowRestartWarning] = useState(false);
        const [warningActionType, setWarningActionType] = useState(null);
        const [pendingStepIndex, setPendingStepIndex] = useState(null);
        const [pendingTimerValue, setPendingTimerValue] = useState(null);

        const refEl = useRef(null);

        useImperativeHandle(ref, () => ({
            isTeamAction: () => {
                setIsSetupSection(true);
            },
            noTeamAction: () => {
                setIsSetupSection(false);
            },
            isMaxRound: () => {
                setIsOpen_warning(true);
            },
            isMaxAlert: () => {
                setWarningType("TIMEOUT");
                setAlert("Maximum time is reached");
            },
            isMaxChar: () => {
                setWarningType("CHAR_LIMIT");
                setAlert("Maximum character is reached");
            },
            isMaxTimer: () => {
                setWarningType("TIMEOUT");
                setAlert("Maximum time is reached");
            },
        }));

        const percentCalc = (stepIndex / (sessionSteps.length - 1)) * 100;

        const [memberList, setMemberList] = useState(
            () => teamMembers || members || [],
        );
        const [isFirstSetup, setIsFirstSetup] = useState(true);
        const [fontClass, setFontClass] = useState(false);

        let textRef = React.createRef();

        const classOpen = isOpen ? "is-open" : "";
        const classBlur = isBlur ? "is-blur" : "";
        const session_total_time = window.sessions?.session_time;
        const useBgImage = Boolean(window.themes.use_background_image);
        const BgImage = window.themes.background_image_theme
            ? window.themes.background_image_theme
            : "";
        const bgColor = window.themes.background_color_theme
            ? window.themes.background_color_theme
            : "#000";
        const bgOpacity = window.themes.overlay_opacity;
        const bgImageStyle = {
            background: `linear-gradient(rgba(0, 0, 0, ${bgOpacity}), rgba(0, 0, 0, ${bgOpacity})), no-repeat
        url(${BgImage})`,
            backgroundSize: "cover",
        };
        const bgColorStyle = {
            background: `linear-gradient(rgba(0, 0, 0, ${bgOpacity}), rgba(0, 0, 0, ${bgOpacity}))`,
            backgroundColor: bgColor,
        };
        const style = useBgImage ? bgImageStyle : bgColorStyle;
        // SETUP TEAM
        const setup_team_subtitle = window.setup_team.subtitle;
        const setup_team_title = window.setup_team.title;
        const setup_team_desc = window.setup_team.desc;
        const setup_team_member_title =
            window.setup_team.setup_team_member_title;
        const setup_team_member_input_placeholder =
            window.setup_team.setup_team_member_input_placeholder;
        const setup_team_member_input_empty_response =
            window.setup_team.setup_team_member_input_empty_response;
        const setup_team_button_back =
            window.setup_team?.button_back || "Back to Timer Setup";

        // WELCOME
        const instruction_title = window.instruction.instruction_title;
        const instruction_subtitle = window.instruction.instruction_subtitle;
        const instruction_text = window.instruction.instruction_text;
        const instruction_next_button_text =
            window.instruction.instruction_next_button_text;
        const instruction_scoring_info =
            window.instruction.instruction_scoring_info;

        const languageCodes = window.wpml_language_codes?.language_codes || [];

        const onChangeTimerValue = (e) => {
            const idx = parseInt(e.target.value, 10);
            const selectedVal = sessionSteps[idx];
            if (selectedVal === undefined) return;

            const gameActive =
                isGameActive !== undefined ? isGameActive : getIsGameActive();

            if (gameActive && selectedVal !== timerValue) {
                setPendingStepIndex(idx);
                setPendingTimerValue(selectedVal);
                setWarningActionType("CHANGE_TIMER");
                setShowRestartWarning(true);
                return;
            }

            setStepIndex(idx);
            setTimerValue(selectedVal);
            setStoredTimer(selectedVal);
            setIsScoringEnabled(true);
            if (onChangeTimer) {
                onChangeTimer(selectedVal);
            }
        };

        const handleBackToTimerSetup = () => {
            const gameActive =
                isGameActive !== undefined ? isGameActive : getIsGameActive();

            if (gameActive) {
                setWarningActionType("BACK_TO_TIMER");
                setShowRestartWarning(true);
                return;
            }

            setIsSetupSection(false);
        };

        const handleCancelRestartWarning = () => {
            setShowRestartWarning(false);
            setPendingStepIndex(null);
            setPendingTimerValue(null);
            setWarningActionType(null);
        };

        const handleConfirmRestartWarning = () => {
            if (warningActionType === "CHANGE_TIMER") {
                if (pendingStepIndex !== null && pendingTimerValue !== null) {
                    setStepIndex(pendingStepIndex);
                    setTimerValue(pendingTimerValue);
                    setStoredTimer(pendingTimerValue);
                    setIsGameActive(false);
                    if (onRestartSession) {
                        onRestartSession(pendingTimerValue);
                    } else if (onChangeTimer) {
                        onChangeTimer(pendingTimerValue);
                    }
                }
            } else if (warningActionType === "BACK_TO_TIMER") {
                setIsGameActive(false);
                if (onRestartSession) {
                    onRestartSession(timerValue);
                }
                setIsSetupSection(false);
            }
            setShowRestartWarning(false);
            setPendingStepIndex(null);
            setPendingTimerValue(null);
            setWarningActionType(null);
        };

        const onInsertMember = (e, teamMemberName) => {
            e.preventDefault();
            const filterSameName = memberList.find(
                (item) => item === teamMemberName,
            );

            if (teamMemberName.trim().length === 0) {
                e.preventDefault();

                setOpen_inputWarning(true);
                setAlert("Cannot Input Empty Field");

                setTimeout(() => {
                    setOpen_inputWarning(false);
                }, 2000);
            } else if (teamMemberName !== filterSameName) {
                setMemberList([...memberList, teamMemberName]);
            } else {
                setAlert(
                    "Sorry, you are not allowed to enter existing username",
                );
                setOpen_inputWarning(true);

                setTimeout(() => {
                    setOpen_inputWarning(false);
                }, 2000);
            }

            setTeamMemberName("");
        };

        const openPassword = () => {
            onOpenPassword();
        };

        const handleOnStart = () => {
            // Prevent onStart when the Modal is hidden
            if (!isOpen) return null;

            const members = memberList;

            // Prevent update feed on update team (only on first setup)
            if (isFirstSetup) {
                axios.post("/wp-json/api/v1/update_feed").catch(console.error);
                setIsFirstSetup(false);
            }

            if (members.length === 0) {
                setOpen_inputWarning(true);
                setAlert(setup_team_member_input_empty_response);

                setTimeout(() => {
                    setOpen_inputWarning(false);
                }, 2000);
            } else {
                const baseUrl = window.themeObj.restURL;
                const gpID = window.themes.user.current_game_progress;
                const apiURL = `${baseUrl}/game/start-game`;
                axios
                    .post(apiURL, {
                        game_progress_id: gpID,
                        time_limit: timerValue,
                    })
                    .then(() => {
                        onStart(members);
                        onClose();
                        onSetPlay();
                        setTimeout(() => {
                            setIsSetupSection(false);
                        }, 1000);
                    })
                    .catch(console.error);
            }
        };

        const handleChange = (e) => {
            if (e.target.value.length >= 25) {
                setOpen_inputWarning(true);
                setAlert("Character limit is 25");
                setDisabled(true);

                setTimeout(() => {
                    setOpen_inputWarning(false);
                    setDisabled(false);
                }, 2000);
            } else {
                setIsOpen_warning(false);
            }

            setTeamMemberName(e.target.value);
        };

        const handleNextPage = () => {
            setIsSetupSection(true);
            openPassword();
            if (!isOpen) return null;
        };

        const handleClose = () => {
            const isTimeout =
                warningType === "TIMEOUT" ||
                (typeof isAlert === "string" &&
                    isAlert.toLowerCase().includes("time"));
            setIsOpen_warning(false);
            setDisabled(false);
            if (typeof isDisable === "function") {
                isDisable();
            }
            if (isTimeout && typeof onFinishGame === "function") {
                onFinishGame();
            }
        };

        const keyListener = (event) => {
            if (refEl.current.classList.contains("is-setup")) {
                if (event.key === "Enter" || event.key === "NumpadEnter") {
                    event.preventDefault();
                    onInsertMember(event, teamMemberName);
                }
            }
        };

        useEffect(() => {
            const currentVal =
                selectedTimer || getStoredTimer(standardSessionTime);
            const idx = getInitialStepIndex(
                sessionSteps,
                currentVal || standardSessionTime,
            );
            setStepIndex(idx);
            const val = sessionSteps[idx] || sessionSteps[0];
            setTimerValue(val);
            setIsScoringEnabled(true);
        }, [sessionSteps, standardSessionTime, selectedTimer]);

        useEffect(() => {
            if (location?.state?.setupTeam) {
                setIsSetupSection(true);
            }
        }, [location?.state]);

        useEffect(() => {
            if (teamMembers && teamMembers.length > 0) {
                setMemberList(teamMembers);
            } else if (members && members.length > 0) {
                setMemberList(members);
            }
        }, [teamMembers, members]);

        useEffect(() => {
            if (login === true) {
                setIsSetupSection(true);
            }

            if (textRef.current && textRef.current.textContent.length >= 900) {
                setFontClass(true);
            } else {
                setFontClass(false);
            }
        }, [timerValue]);

        return (
            <>
                <div
                    ref={refEl}
                    className={`introduction-screen ${classOpen} ${classBlur} ${
                        isSetupSection ? "is-setup" : ""
                    }`}
                    // style={style}
                >
                    <DecorativeLines3 variant="intro" label="INTRO" />
                    <div className="introduction-screen__left">
                        <div className="introduction-screen__box">
                            <div className="introduction-screen__content introduction-screen__content__instruction">
                                <div className="instruction-content">
                                    <h3 className="instruction-content__subtitle">
                                        {instruction_subtitle}
                                    </h3>
                                    <h1 className="instruction-content__title">
                                        {instruction_title}
                                    </h1>
                                    <div
                                        className={`instruction-content__text ${
                                            fontClass ? "high" : ""
                                        }`}
                                        dangerouslySetInnerHTML={{
                                            __html: instruction_text,
                                        }}
                                        ref={textRef}
                                    />
                                </div>
                                <div className="instruction-session">
                                    <div
                                        className="instruction-session__input"
                                        style={{
                                            "--timer-percent": `${percentCalc}%`,
                                        }}
                                    >
                                        <div className="instruction-session__step-labels">
                                            {sessionSteps.map(
                                                (stepVal, index) => {
                                                    const pct =
                                                        (index /
                                                            (sessionSteps.length -
                                                                1)) *
                                                        100;
                                                    return (
                                                        <span
                                                            key={index}
                                                            className="instruction-session__step-label"
                                                            style={{
                                                                left: `${pct}%`,
                                                            }}
                                                        >
                                                            {stepVal}
                                                        </span>
                                                    );
                                                },
                                            )}
                                        </div>
                                        <div className="instruction-session__track-bar" />
                                        <div className="instruction-session__separators">
                                            {sessionSteps.map((_, index) => {
                                                const pct =
                                                    (index /
                                                        (sessionSteps.length -
                                                            1)) *
                                                    100;
                                                return (
                                                    <span
                                                        className="instruction-session__separator-line"
                                                        key={index}
                                                        style={{
                                                            left: `${pct}%`,
                                                        }}
                                                    />
                                                );
                                            })}
                                        </div>
                                        <input
                                            className="input-action"
                                            type="range"
                                            value={stepIndex}
                                            min={0}
                                            max={sessionSteps.length - 1}
                                            step={1}
                                            onChange={onChangeTimerValue}
                                        />
                                        <div
                                            className="instruction-session__thumb-value"
                                            style={{
                                                left: `${percentCalc}%`,
                                            }}
                                        >
                                            <span className="thumbVisual">
                                                <svg
                                                    width="40"
                                                    height="40"
                                                    viewBox="0 0 36 41"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M10.9604 7.0502L18.0107 0V14.1004L10.9604 7.0502Z"
                                                        fill="white"
                                                    />
                                                    <path
                                                        d="M24.9893 7.0502L17.9391 0V14.1004L24.9893 7.0502Z"
                                                        fill="#C7D0DE"
                                                    />
                                                    <path
                                                        d="M32 19C32 17.1615 31.6379 15.341 30.9343 13.6424C30.2307 11.9438 29.1995 10.4005 27.8995 9.10047C26.5995 7.80045 25.0561 6.76922 23.3576 6.06566C21.659 5.36209 19.8385 4.99997 18 4.99997L18 19H32Z"
                                                        fill="#C7D0DE"
                                                    />
                                                    <path
                                                        d="M4 19C4 20.8385 4.36212 22.659 5.06569 24.3575C5.76925 26.0561 6.80049 27.5994 8.10051 28.8995C9.40053 30.1995 10.9439 31.2307 12.6424 31.9343C14.341 32.6378 16.1615 33 18 33L18 19H4Z"
                                                        fill="#C7D0DE"
                                                    />
                                                    <path
                                                        d="M18 4.99997C16.1615 4.99997 14.341 5.36209 12.6424 6.06566C10.9439 6.76922 9.40053 7.80046 8.1005 9.10048C6.80048 10.4005 5.76925 11.9438 5.06569 13.6424C4.36212 15.341 4 17.1615 4 19L18 19V4.99997Z"
                                                        fill="white"
                                                    />
                                                    <path
                                                        d="M18 33C19.8385 33 21.659 32.6378 23.3576 31.9343C25.0561 31.2307 26.5995 30.1995 27.8995 28.8995C29.1995 27.5994 30.2307 26.0561 30.9343 24.3575C31.6379 22.659 32 20.8385 32 19L18 19V33Z"
                                                        fill="white"
                                                    />
                                                    <circle
                                                        opacity="0.5"
                                                        cx="18.5"
                                                        cy="19.5"
                                                        r="10.5"
                                                        fill="#BDC9DB"
                                                    />
                                                </svg>
                                            </span>
                                            <span className="instruction-session__title-timer">
                                                {timerValue}
                                                <span className="instruction-session__title--bold-2">
                                                    min
                                                </span>
                                            </span>
                                        </div>
                                        <div className="instruction-session__meta-row">
                                            <span className="instruction-session__minutes-tag">
                                                minutes
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {languageCodes.length > 1 && (
                                    <LanguageOptionMenu />
                                )}

                                <UserConsumer>
                                    {(value) =>
                                        usePassword ? (
                                            <Link
                                                className="finish-session-button"
                                                to="/password-page"
                                                state={{ value }}
                                            >
                                                <Button
                                                    className="play"
                                                    text={
                                                        instruction_next_button_text
                                                    }
                                                    onClick={handleNextPage}
                                                />
                                            </Link>
                                        ) : (
                                            <Button
                                                className="play"
                                                text={
                                                    instruction_next_button_text
                                                }
                                                onClick={handleNextPage}
                                            />
                                        )
                                    }
                                </UserConsumer>
                                <Link
                                    to="/question-select"
                                    style={{ textDecoration: "none" }}
                                >
                                    <button
                                        type="button"
                                        className="instruction-setup__back-button"
                                    >
                                        <FontAwesomeIcon
                                            icon={faArrowLeft}
                                            className="back-icon"
                                        />
                                        <span>
                                            {window.instruction?.button_back ||
                                                "Back to question selection"}
                                        </span>
                                    </button>
                                </Link>
                            </div>
                            <div className="introduction-screen__content introduction-screen__content__setup team">
                                <div className="instruction-content">
                                    <h3
                                        className="instruction-content__subtitle"
                                        style={{
                                            left: `${percentCalc}%`,
                                        }}
                                    >
                                        {setup_team_subtitle}
                                    </h3>
                                    <h1 className="instruction-content__title">
                                        {setup_team_title}
                                    </h1>
                                    <div
                                        className="instruction-content__text"
                                        dangerouslySetInnerHTML={{
                                            __html: setup_team_desc,
                                        }}
                                    />
                                </div>
                                {/* <div className="instruction-setup__input">
                      <h3 className="instruction-setup__title">Team Name</h3>
                      <div className="instruction-setup__input-container">
                        <input
                          className="instruction-setup__input-team"
                          type="text"
                          placeholder="Team Name"
                          onChange={e => setTeamName(e.target.value)}
                        />
                      </div>
                    </div> */}
                                <div className="instruction-setup__input">
                                    <h3 className="instruction-setup__title">
                                        {setup_team_member_title}
                                    </h3>
                                    <div
                                        className={`instruction-setup__input-container ${
                                            teamMemberName &&
                                            teamMemberName.trim().length > 0
                                                ? "has-value"
                                                : ""
                                        }`}
                                    >
                                        <input
                                            onKeyDown={keyListener}
                                            className="instruction-setup__input-member"
                                            type="text"
                                            placeholder={
                                                setup_team_member_input_placeholder
                                            }
                                            value={teamMemberName}
                                            onChange={handleChange}
                                            disabled={isDisabled}
                                        />
                                        <button
                                            type="button"
                                            onClick={(e) =>
                                                onInsertMember(
                                                    e,
                                                    teamMemberName,
                                                )
                                            }
                                            className={`instruction-setup__add-button ${
                                                teamMemberName &&
                                                teamMemberName.trim().length > 0
                                                    ? "is-active"
                                                    : ""
                                            }`}
                                        >
                                            +
                                        </button>
                                    </div>
                                    <div
                                        className={
                                            isOpen_inputWarning
                                                ? `input-warning`
                                                : `close-warning`
                                        }
                                    >
                                        <p>{isAlert}</p>
                                    </div>
                                </div>
                                <div className="instruction-setup__member-list">
                                    {memberList.map((item, index) => (
                                        <Item
                                            key={index}
                                            item={item}
                                            removeItem={() =>
                                                setMemberList(
                                                    memberList.filter(
                                                        (member) =>
                                                            member !== item,
                                                    ),
                                                )
                                            }
                                        />
                                    ))}
                                </div>
                                <Button
                                    className="play"
                                    {...playGameButton}
                                    onClick={handleOnStart}
                                />
                                <button
                                    type="button"
                                    className="instruction-setup__back-button"
                                    onClick={handleBackToTimerSetup}
                                >
                                    <FontAwesomeIcon
                                        icon={faArrowLeft}
                                        className="back-icon"
                                    />
                                    <span>{setup_team_button_back}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="introduction-screen__spinner">
                        <Wheel
                            members={members}
                            data={categoryList}
                            {...wheel}
                            diameter={500}
                        />
                        <ProgressBar
                            currentRound="-"
                            totalRound=""
                            isDisplayContent={false}
                        />
                    </div>
                </div>
                {showRestartWarning && (
                    <div className="restart-session-modal-overlay">
                        <div className="restart-session-modal">
                            <h3 className="restart-session-modal__title">
                                {window.setup_team?.reset_game_progress_popup
                                    ?.title || "Change Session Time?"}
                            </h3>
                            <p className="restart-session-modal__desc">
                                {
                                    window.setup_team?.reset_game_progress_popup
                                        ?.subtitle
                                }
                            </p>
                            <div className="restart-session-modal__actions">
                                <button
                                    type="button"
                                    className="restart-session-modal__btn restart-session-modal__btn--cancel"
                                    onClick={handleCancelRestartWarning}
                                >
                                    {window.setup_team
                                        ?.reset_game_progress_popup
                                        ?.cancel_button_text || "Cancel"}
                                </button>
                                <button
                                    type="button"
                                    className="restart-session-modal__btn restart-session-modal__btn--confirm"
                                    onClick={handleConfirmRestartWarning}
                                >
                                    {window.setup_team
                                        ?.reset_game_progress_popup
                                        ?.restart_button_text ||
                                        "Restart Session"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {isOpen_warning && (
                    <div className="restart-session-modal-overlay">
                        <div className="restart-session-modal">
                            <div className="restart-session-modal__icon">
                                {warningType === "TIMEOUT" ||
                                (typeof isAlert === "string" &&
                                    isAlert.toLowerCase().includes("time")) ? (
                                    <svg
                                        width="32"
                                        height="32"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="#FDD100"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <circle cx="12" cy="12" r="10" />
                                        <polyline points="12 6 12 12 16 14" />
                                    </svg>
                                ) : (
                                    <svg
                                        width="32"
                                        height="32"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="#FDD100"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                        <line x1="12" y1="9" x2="12" y2="13" />
                                        <line
                                            x1="12"
                                            y1="17"
                                            x2="12.01"
                                            y2="17"
                                        />
                                    </svg>
                                )}
                            </div>
                            <h3 className="restart-session-modal__title">
                                {warningType === "TIMEOUT" ||
                                (typeof isAlert === "string" &&
                                    isAlert.toLowerCase().includes("time"))
                                    ? "Time's Up!"
                                    : isAlert || "Notice"}
                            </h3>
                            <p className="restart-session-modal__desc">
                                {warningType === "TIMEOUT" ||
                                (typeof isAlert === "string" &&
                                    isAlert.toLowerCase().includes("time"))
                                    ? "The session time limit has been reached. Click below to finish the session and view the results."
                                    : isAlert}
                            </p>
                            <div className="restart-session-modal__actions">
                                <button
                                    type="button"
                                    className="restart-session-modal__btn restart-session-modal__btn--confirm"
                                    onClick={handleClose}
                                >
                                    {warningType === "TIMEOUT" ||
                                    (typeof isAlert === "string" &&
                                        isAlert.toLowerCase().includes("time"))
                                        ? "Finish Session"
                                        : "Close"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </>
        );
    },
);

export default introductionScreen;
